import axios from 'axios';
import { getTokenData, saveTokenData, setAuthMode, getAuthMode, AUTH_MODE, clearTokenData } from '../utils/token';

export default (() => {
  const clientId = process.env.VUE_APP_CONDUCTOR_CLIENT_ID || '';
  const basePath = process.env.VUE_APP_AUTH_SERVER || '';
  const accessServerURL = process.env.VUE_APP_ACCESS_SERVER || '';
  const tokenPath = '/oauth/token';
  const accessPath = '/access';
  const accessV2Path = '/access/v2';

  let refreshTokenInProgress;
  let refreshTokenPromise;

  function saveToken(data) {
    const tokenData = Object.assign({ last_refresh: Date.now() }, data);
    
    // Convert expires_in to milliseconds if it's in seconds
    if (tokenData.expires_in && tokenData.expires_in < 1000000) {
      tokenData.expires_in = tokenData.expires_in * 1000;
    }
    
    saveTokenData(tokenData);
    return tokenData;
  }

  function saveJwt(token){
    const tokenData = {
      access_token: token,
      token_type: "bearer",
    }
    saveTokenData(tokenData);
    return tokenData;
  }

   /**
   * Login with httpOnly cookies (ONLY METHOD)
   * Uses /sso/proxy-login2 endpoint that sets httpOnly cookies
   * NO FALLBACK to localStorage - forces all users to use secure cookies
   */
  async function login(username, password, retryCount = 0) {
    const credentials = `${username}:${password}`;
    const encodedCredentials = btoa(credentials);
    const maxRetries = 1;
    
    const cookieUrl = accessServerURL
      .concat(accessPath)
      .concat('/sso/proxy-login2');
    
    console.log('🍪 [AUTH] Attempting login with HttpOnly cookies:', { 
      url: cookieUrl,
      attempt: retryCount + 1
    });
    
    try {
      const response = await axios({
        method: 'post',
        url: cookieUrl,
        data: {  
          clientId: clientId,
          encodedCredentials: encodedCredentials
        },
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
        timeout: 10000
      });
      
      console.log('✅ [AUTH] Login successful with cookies - Response details:', {
        status: response.status,
        tokenType: response.data?.token_type,
        expiresIn: response.data?.expires_in,
        scope: response.data?.scope
      });
      
      // In HttpOnly cookie mode, tokens are in cookies (not in JSON response)
      // Backend returns metadata: token_type, expires_in, scope
      if (!response.data || !response.data.token_type || !response.data.expires_in) {
        console.error('❌ [AUTH] Invalid login response - missing token metadata:', response.data);
        throw new Error('Invalid login response: missing token metadata');
      }
      
      console.log('✅ [AUTH] Login validation passed - tokens set in HttpOnly cookies');
      setAuthMode(AUTH_MODE.COOKIES);
      return saveToken(response.data);
      
    } catch (error) {
      console.error('❌ [AUTH] Cookie login failed:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        isNetworkError: !error.response,
        attempt: retryCount + 1
      });
      
      // Reintentar solo si es error de red (no 401/403)
      const isNetworkError = !error.response || error.response.status >= 500;
      const shouldRetry = isNetworkError && retryCount < maxRetries;
      
      if (shouldRetry) {
        console.warn(`⚠️ [AUTH] Network error, retrying... (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return login(username, password, retryCount + 1);
      }
      
      throw error;
    }
  }

  async function SSO(username, host) {
    const url = accessServerURL
      .concat(accessPath)
      .concat('/sso/login');
    const response = await axios({
      method: 'get',
      url,
      params: {
        email: username,
        relayState: host,
      },
    });
    return response.data;
  }

  function checkLoggedIn() {
    const tokenData = getTokenData();
    const mode = getAuthMode();

    if (mode === AUTH_MODE.COOKIES) {
      const hasCookieSession =
        !!tokenData &&
        (tokenData.tokensInCookies === true || tokenData.mode === AUTH_MODE.COOKIES);
      return hasCookieSession ? tokenData : null;
    }

    if (tokenData && tokenData.access_token) {
      return tokenData;
    }
    return null;
  }

  async function logout() {
    const mode = getAuthMode();
    
    // Call appropriate logout endpoint
    if (mode === AUTH_MODE.COOKIES) {
      try {
        const url = accessServerURL
          .concat(accessPath)
          .concat('/sso/proxy-logout2');
        
        await axios({
          method: 'post',
          url,
          withCredentials: true // Send cookies to be cleared
        });
        console.log('✅ Cookie logout successful');
      } catch (error) {
        console.error('❌ Cookie logout endpoint failed:', error.message);
        console.error('❌ CRITICAL: HttpOnly cookies CANNOT be cleared from JavaScript (browser security)');
        console.error('❌ Backend must handle cookie cleanup - frontend cannot do this');
        console.warn('⚠️ User may have stale cookies until backend logout succeeds or cookies expire');
        console.warn('⚠️ If re-login fails, backend team must investigate logout endpoint');
      }
    }
    
    // Clear all local data
    clearTokenData();
    localStorage.removeItem('my-app');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('orgId');
    localStorage.removeItem('chmln:l.C.d');
    localStorage.removeItem('siteId');

		// Clear portal banner dismiss state so a fresh login in the same tab
		// will show active notifications again.
		try {
			for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
				const key = sessionStorage.key(i);
				if (key && key.startsWith('banner_dismissed_')) {
					sessionStorage.removeItem(key);
				}
			}
		} catch (e) {
			// ignore storage access errors
		}
    
    console.log('🗑️ Logout complete, reloading page...');
    //reload needed to reset Google Analytics, if not reseted and logged in with a different account he old user-id will persist
    window.location.reload();
  }

  async function forgot(username) {
    const url = accessServerURL
      .concat(accessV2Path)
      .concat('/users/forgotPassword');
    const response = await axios({
      method: 'get',
      url,
      params: {
        email: username,
        clientId: clientId,
      },
    });
    return response.data;
  }

  async function reset(username, password, key) {
    const url = accessServerURL.concat(accessPath).concat('/users/verify');
    const response = await axios({
      method: 'post',
      url,
      data: {
        email: username,
        newPassword: password,
        verificationKey: key,
      },
    });
    return response.data;
  }

  /**
   * Refreshes the Access and Refresh Tokens
   *
   * This function only uses only concurrent api call, it reuses a pending api call if it is in progress
   *
   * @returns {Promise} Promise of Token data object
   */
    async function refreshToken() {
    if (!refreshTokenInProgress) {
      console.log('🔄 [AUTH] Starting new refresh token flow');
      refreshTokenInProgress = true;
      refreshTokenPromise = new Promise((resolve, reject) => {
        refreshTokenCall()
          .then(function (response) {
            console.log('✅ [AUTH] Refresh token flow completed');
            resolve(response);
          })
          .catch(function (error) {
            console.error('❌ [AUTH] Refresh token flow failed:', {
              message: error.message,
              status: error.response?.status
            });
            reject(error);
          })
          .finally(function () {
            refreshTokenInProgress = false;
            console.log('🔄 [AUTH] Refresh flag reset');
          });
      });
    } else {
      console.log('⏳ [AUTH] Refresh already in progress, waiting...');
    }
    return refreshTokenPromise;
  }

  async function refreshTokenCall() {
    const mode = getAuthMode();
    
    console.log('🔄 [AUTH] refreshTokenCall started:', { mode, timestamp: new Date().toISOString() });
    
    
    if (mode !== AUTH_MODE.COOKIES) {
      console.error('❌ [AUTH] Invalid auth mode:', mode);
      throw new Error('Only cookie-based authentication is supported');
    }
    
    try {
      const cookieUrl = accessServerURL
        .concat(accessPath)
        .concat('/sso/proxy-refresh2');
      
      console.log('🍪 [AUTH] Attempting cookie-based refresh:', { url: cookieUrl });
      console.log('🍪 [AUTH] Request config:', {
        method: 'POST',
        clientId: clientId,
        withCredentials: true,
        timeout: 10000
      });
      
      const response = await axios({
        method: 'post',
        url: cookieUrl,
        data: { clientId: clientId },
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
        timeout: 10000
      });
      
      console.log('✅ [AUTH] Cookie refresh successful - Response details:', {
        status: response.status,
        statusText: response.statusText,
        hasData: !!response.data,
        tokenType: response.data?.token_type,
        expiresIn: response.data?.expires_in,
        scope: response.data?.scope,
        responseDataKeys: response.data ? Object.keys(response.data) : []
      });
      
      // CRITICAL FIX: In HttpOnly cookie mode, tokens are NOT in JSON response
      // Tokens are in HttpOnly cookies (not accessible from JavaScript)
      // Backend returns only METADATA in JSON: token_type, expires_in, scope
      // Validate metadata instead of tokens
      if (!response.data || !response.data.token_type || !response.data.expires_in) {
        console.error('❌ [AUTH] Invalid refresh response - missing token metadata:', response.data);
        throw new Error('Invalid refresh response: missing token metadata (token_type or expires_in)');
      }
      
      console.log('✅ [AUTH] Refresh validation passed - tokens updated in HttpOnly cookies');
      console.log('📊 [AUTH] Token metadata:', {
        tokenType: response.data.token_type,
        expiresIn: response.data.expires_in,
        scope: response.data.scope
      });
      
      // Save only metadata (tokens are in cookies)
      return saveToken(response.data);
      
    } catch (cookieError) {
      console.error('❌ [AUTH] Cookie refresh failed - Full error details:', {
        message: cookieError.message,
        status: cookieError.response?.status,
        statusText: cookieError.response?.statusText,
        data: cookieError.response?.data,
        headers: cookieError.response?.headers,
        isNetworkError: !cookieError.response,
        isTimeout: cookieError.code === 'ECONNABORTED',
        errorCode: cookieError.code,
        url: cookieError.config?.url
      });
      throw cookieError;
    }
  }
  /**
   * Check if any Refresh Token call is in progress and wait for it
   *
   * @returns {Promise<undefined>} Promise which will resolve when no Refresh Token call is in progress
   */
  async function checkRefreshCallNotPending() {
    if (refreshTokenInProgress) {
      try {
        await refreshToken();
      } catch (e) {
        e;
      }
    }
  }

  /**
   * Gets the stored Access Token immediately but does not checks for an ongoing Refresh Token call
   *
   * The Access Token returned from this function may or may not be invalidated due to
   * any ongoing Refresh Token call and can also be expired
   *
   * Usage of this function is not recommended, you should instead use the Promise version
   * @see getAccessToken for the Promise version of Get Access Token
   *
   * @returns {string|null} Access Token which can be null if not present in application
   */
  function getAccessTokenSync() {
    const tokenData = getTokenData();
    const mode = getAuthMode();
    
    // In cookies mode, tokens are in httpOnly cookies (not accessible to JS)
    // Return null so axios interceptor doesn't add Authorization header
    // HybridAuthFilter in backend will read cookies and add the header
    if (mode === AUTH_MODE.COOKIES) {
      return null;
    }
    
    // In localStorage mode, return the token
    return (tokenData && tokenData.access_token) || null;
  }

  /**
   * Gets the stored Access Token but checks for an ongoing Refresh Token call first
   *
   * The Access Token returned from this function will not be invalidated due to
   * any ongoing Refresh Token call but can be expired
   *
   * @returns {Promise<string|null>} Promise of an Access Token, which can be null if not present in application
   */
  async function getAccessToken() {
    await checkRefreshCallNotPending();
    return getAccessTokenSync();
  }

  function getRefreshTokenSync() {
    const tokenData = getTokenData();
    const mode = getAuthMode();
    
    // In cookies mode, refresh_token is in httpOnly cookie
    if (mode === AUTH_MODE.COOKIES) {
      return null;
    }
    
    // In localStorage mode, return the refresh token
    return (tokenData && tokenData.refresh_token) || null;
  }

  async function getTableauToken() {
    const payload = {
      userName: 'temp'
    };

    const url = "https://c2pesp01-usea1b.link-labs.com/auth";
    const response = await axios({
      method: 'post',
      url,
      data: payload,
      withCredentials: false // Disable credentials for Tableau server (CORS wildcard)
    });
    return response.data;
  }

  return {
    login,
    SSO,
    saveJwt,
    checkLoggedIn,
    logout,
    forgot,
    reset,
    refreshToken,
    getAccessTokenSync,
    getAccessToken,
    getRefreshTokenSync,
    getTableauToken,
  };
})();
